import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Copy, LogOut } from 'lucide-react';
import { createInvite, listInvites, updateMe, type CreateInviteResponse } from '@/entities/user';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { PageHeader } from '@/widgets/page-header';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

const nameSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(1, 'Confirm your new password'),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type NameValues = z.infer<typeof nameSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function ProfilePage() {
  const { user, hasPermission, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const canInvite = hasPermission('can_invite_users');
  const queryClient = useQueryClient();
  const [inviteRole, setInviteRole] = useState<'admin' | 'teacher'>('teacher');
  const [lastInvite, setLastInvite] = useState<CreateInviteResponse | null>(null);

  const nameForm = useForm<NameValues>({
    resolver: zodResolver(nameSchema),
    values: { name: user?.name ?? '' },
  });

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const nameMutation = useMutation({
    mutationFn: (values: NameValues) => updateMe({ name: values.name }),
    onSuccess: (next) => {
      updateUser(next);
      toast.success('Name updated');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Update failed');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: PasswordValues) =>
      updateMe({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: () => {
      passwordForm.reset();
      toast.success('Password updated');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Update failed');
    },
  });

  const invitesQuery = useQuery({
    queryKey: ['invites'],
    queryFn: listInvites,
    enabled: canInvite,
  });

  const createInviteMutation = useMutation({
    mutationFn: () => createInvite(inviteRole),
    onSuccess: (invite) => {
      setLastInvite(invite);
      toast.success('Invite link created');
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Failed to create invite');
    },
  });

  async function copyInviteUrl(path: string) {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy link');
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Profile" description="Account settings and invitations" />
      <div className="flex-1 space-y-6 overflow-auto p-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Account</CardTitle>
            <CardDescription>Update your display name and password.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <p className="text-sm font-medium">Email</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Role</p>
              <p className="text-sm text-muted-foreground">{user?.role_key}</p>
            </div>

            <Form {...nameForm}>
              <form
                className="flex max-w-md flex-col gap-3"
                onSubmit={nameForm.handleSubmit((values) => nameMutation.mutate(values))}
              >
                <FormField
                  control={nameForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input autoComplete="name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-fit" disabled={nameMutation.isPending}>
                  {nameMutation.isPending ? 'Saving…' : 'Save name'}
                </Button>
              </form>
            </Form>

            <Form {...passwordForm}>
              <form
                className="flex max-w-md flex-col gap-3"
                onSubmit={passwordForm.handleSubmit((values) => passwordMutation.mutate(values))}
              >
                <FormField
                  control={passwordForm.control}
                  name="current_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm new password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-fit" disabled={passwordMutation.isPending}>
                  {passwordMutation.isPending ? 'Updating…' : 'Change password'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {canInvite ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invite users</CardTitle>
              <CardDescription>
                Create a single-use link. The invitee chooses their email and password; no email
                confirmation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Role</p>
                  <Select
                    value={inviteRole}
                    onValueChange={(value) => {
                      if (value === 'admin' || value === 'teacher') setInviteRole(value);
                    }}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teacher">teacher</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => createInviteMutation.mutate()}
                  disabled={createInviteMutation.isPending}
                >
                  {createInviteMutation.isPending ? 'Creating…' : 'Create invite link'}
                </Button>
              </div>

              {lastInvite ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                  <code className="min-w-0 flex-1 break-all">
                    {`${window.location.origin}${lastInvite.invite_path}`}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void copyInviteUrl(lastInvite.invite_path)}
                  >
                    <Copy className="size-4" />
                    Copy
                  </Button>
                </div>
              ) : null}

              <div className="space-y-2">
                <p className="text-sm font-medium">Pending invites</p>
                {invitesQuery.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : invitesQuery.isError ? (
                  <p className="text-sm text-destructive">
                    {invitesQuery.error instanceof ApiError
                      ? invitesQuery.error.message
                      : 'Failed to load invites'}
                  </p>
                ) : (invitesQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">No pending invites.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Role</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitesQuery.data?.map((invite) => (
                        <TableRow key={invite.id}>
                          <TableCell>{invite.role_key}</TableCell>
                          <TableCell>{formatDate(invite.expires_at)}</TableCell>
                          <TableCell>{formatDate(invite.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Button
          variant="outline"
          onClick={() => {
            logout();
            navigate('/login');
          }}
        >
          <LogOut />
          Log out
        </Button>
      </div>
    </div>
  );
}
